/**
 * Digital Storage Role - Constants
 *
 * Fatores de conversão, símbolos e aliases para unidades de armazenamento digital.
 *
 * Fontes:
 * - IEC 60027-2 (prefixos binários)
 * - SI Brochure, 9th Edition (2019)
 *
 * IMPORTANTE: Esta biblioteca suporta AMBOS os sistemas:
 * - SI (decimal): kB, MB, GB, TB (base 1000)
 * - IEC (binário): KiB, MiB, GiB, TiB (base 1024)
 */

import type { SimpleUnitConfig, UnitAliases } from "../../contracts";

// =============================================================================
// TYPES
// =============================================================================

export type DigitalUnit =
  // Fundamental
  | "bit"
  | "byte"
  // IEC Binary (base 1024)
  | "kibibyte"
  | "mebibyte"
  | "gibibyte"
  | "tebibyte"
  | "pebibyte"
  | "exbibyte"
  // SI Decimal (base 1000)
  | "kilobyte"
  | "megabyte"
  | "gigabyte"
  | "terabyte"
  | "petabyte"
  | "exabyte";

// =============================================================================
// UNITS CONFIG
// =============================================================================

/**
 * Configuração de cada unidade de armazenamento digital.
 *
 * TODOS os fatores são EXATOS:
 * - IEC: potências de 2 (1024^n)
 * - SI: potências de 10 (1000^n)
 *
 * Convenção:
 * - byte é a BASE (fator = 1)
 * - bit = 1/8 byte (fator = 0.125)
 */
export const DIGITAL_UNITS: Record<DigitalUnit, SimpleUnitConfig> = {
  // Fundamental
  bit: {
    factor: 0.125, // 1/8 byte
    symbol: "b",
    singular: "bit",
    plural: "bits",
  },
  byte: {
    factor: 1,
    symbol: "B",
    singular: "byte",
    plural: "bytes",
  },

  // IEC Binary (base 1024) - RAM, cache, file systems
  kibibyte: {
    factor: 1024, // 2^10
    symbol: "KiB",
    singular: "kibibyte",
    plural: "kibibytes",
  },
  mebibyte: {
    factor: 1048576, // 2^20
    symbol: "MiB",
    singular: "mebibyte",
    plural: "mebibytes",
  },
  gibibyte: {
    factor: 1073741824, // 2^30
    symbol: "GiB",
    singular: "gibibyte",
    plural: "gibibytes",
  },
  tebibyte: {
    factor: 1099511627776, // 2^40
    symbol: "TiB",
    singular: "tebibyte",
    plural: "tebibytes",
  },
  pebibyte: {
    factor: 1125899906842624, // 2^50
    symbol: "PiB",
    singular: "pebibyte",
    plural: "pebibytes",
  },
  exbibyte: {
    factor: 1152921504606846976, // 2^60
    symbol: "EiB",
    singular: "exbibyte",
    plural: "exbibytes",
  },

  // SI Decimal (base 1000) - HD marketing, network speeds
  kilobyte: {
    factor: 1000, // 10^3
    symbol: "kB",
    singular: "kilobyte",
    plural: "kilobytes",
  },
  megabyte: {
    factor: 1000000, // 10^6
    symbol: "MB",
    singular: "megabyte",
    plural: "megabytes",
  },
  gigabyte: {
    factor: 1000000000, // 10^9
    symbol: "GB",
    singular: "gigabyte",
    plural: "gigabytes",
  },
  terabyte: {
    factor: 1000000000000, // 10^12
    symbol: "TB",
    singular: "terabyte",
    plural: "terabytes",
  },
  petabyte: {
    factor: 1000000000000000, // 10^15
    symbol: "PB",
    singular: "petabyte",
    plural: "petabytes",
  },
  exabyte: {
    factor: 1000000000000000000, // 10^18
    symbol: "EB",
    singular: "exabyte",
    plural: "exabytes",
  },
};

// =============================================================================
// ALIASES
// =============================================================================

/**
 * Aliases para reconhecimento de unidades no cast
 */
export const DIGITAL_ALIASES: UnitAliases = {
  // bit
  b: "bit",
  // byte
  B: "byte",
  // kibibyte (IEC)
  KiB: "kibibyte",
  // mebibyte (IEC)
  MiB: "mebibyte",
  // gibibyte (IEC)
  GiB: "gibibyte",
  // tebibyte (IEC)
  TiB: "tebibyte",
  // pebibyte (IEC)
  PiB: "pebibyte",
  // exbibyte (IEC)
  EiB: "exbibyte",
  // kilobyte (SI)
  kB: "kilobyte",
  kb: "kilobyte",
  // megabyte (SI)
  MB: "megabyte",
  // gigabyte (SI)
  GB: "gigabyte",
  // terabyte (SI)
  TB: "terabyte",
  // petabyte (SI)
  PB: "petabyte",
  // exabyte (SI)
  EB: "exabyte",
};

// =============================================================================
// HELPERS
// =============================================================================

/** Lista de todas as unidades */
export const DIGITAL_UNIT_LIST = Object.keys(DIGITAL_UNITS) as DigitalUnit[];

/** Fatores de conversão (para compatibilidade) */
export const DIGITAL_FACTORS: Record<DigitalUnit, number> = Object.fromEntries(
  Object.entries(DIGITAL_UNITS).map(([unit, config]) => [
    unit,
    config.factor ?? 1,
  ]),
) as Record<DigitalUnit, number>;
